import React, { useState, useContext, useMemo } from "react";
import cx from "classnames";
import {
  Heading,
  RoundButton,
  Link,
  Text,
  Tooltip,
  DonutLegend,
  SkeletonBase,
} from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { motion } from "motion/react";
import { useHistory, useLocation } from "react-router-dom";
import { ContextEDH } from "@context/ContextEDH";
import { Package } from "@models/ISchema";
import { Context } from "../../context/GroupsManagerContext";
import { Types as NavigationTypes } from "../../context/navigation.reducer";
import { Types as FilterSelectorsTypes } from "../../context/filtersSelectors.reducer";
import { Types as FilterTypes } from "../../context/filters.reducer";
import { AdaptedCourse, AdaptedGroup, FiltersType } from "../../models";
import * as mouseflow from "@utils/mouseflow";
import donut, { getDonutRows } from "../../utils/donut";
import { getTotal, getUsed, getWaiting } from "../../utils/groupCapacityStats";
import ScheduleSummary from "./ScheduleSummary";
import Donut from "../../components/Donut/Donut";
import capacityCalculationReasonText from "../../utils/capacityCalculationReasonText";
import useContextUrl from "@common/hooks/useContextUrl";
import { useTranslation } from "react-i18next";
import css from "./groupDetail.module.scss";

const COURSE_TYPE_TAG = "itesm.mx/course_type_name";

interface Props {
  capacitiesSumUp: number;
  displayCapacitiesError: boolean;
  isLoading?: boolean;
  group?: AdaptedGroup;
  groups?: AdaptedGroup[];
}

const GroupDetail: React.FC<Props> = ({
  capacitiesSumUp,
  displayCapacitiesError,
  isLoading = false,
  group,
  groups,
}) => {
  const { t } = useTranslation();
  const { state: outerState } = useContext(ContextEDH);
  const { state, dispatch } = useContext(Context);
  const location = useLocation();
  const history = useHistory();
  const [displayInfo, setDisplayInfo] = useState(false);
  const { contextUrl } = useContextUrl();

  const isDefaultBehavior = outerState?.base?.groupsManagerType === "SHARED";

  const historyChangesUrl = `/editor/groups-manager/change_history/${contextUrl}/${
    group?.id
  }?isReferent=${!!group?.isReferent}${group?.clientCode ? `&code=${group?.clientCode}` : ""}${
    group?.link ? `&linkId=${group?.link?.id}` : ""
  }`;

  const isLoadingData = isLoading || !group;

  const departments = useMemo((): string => {
    if (!group) return "-";

    const departments = (group?.subject?.academicUnits as any)?.departments ?? [];
    if (departments.length === 0) return "--";
    return departments.map(department => department.name).join(", ");
  }, [group]);

  const schools = useMemo((): string => {
    if (!group) return "-";

    const schools = (group?.subject?.academicUnits as any)?.schools ?? [];
    if (schools.length === 0) return "--";
    return schools.map(school => school.name).join(", ");
  }, [group]);

  const efficiency = useMemo((): string => {
    const min = group?.groupCapacity?.configMinCapacity ?? 0;
    const max = group?.groupCapacity?.configMaxCapacity ?? 0;
    return !min || !max ? "N/A" : `${min} - ${max}`;
  }, [group]);

  const groupLabels = useMemo((): string => {
    const labels = group?.labels;
    return (labels ?? []).map(label => label.code).join(", ");
  }, [group]);

  const courseTypeName = useMemo((): string => {
    const course = group?.course ?? ({} as AdaptedCourse);
    const tags = course?.tags ?? {};
    return tags[COURSE_TYPE_TAG] ? (tags[COURSE_TYPE_TAG] as string) : "--";
  }, [group]);

  const groupsDonutStats = useMemo(() => {
    const stats = {
      total: 0,
      used: 0,
      waiting: 0,
    };

    if (isLoadingData) {
      return getDonutRows(stats);
    }

    if (!groups?.length) return [];

    groups?.forEach(gp => {
      const total = getTotal(gp) ?? 0;
      const used = getUsed(gp) ?? 0;
      const waiting = getWaiting(gp) ?? 0;

      stats.total += total;
      stats.used += used;
      stats.waiting += waiting;
    });

    return getDonutRows(stats);
  }, [group, groups, isLoadingData]);

  const handlePackageClick = (item: Package) => {
    dispatch({ type: FilterTypes.CleanFilters });
    dispatch({ type: FilterTypes.SelectPackage, payload: item?.id });
    dispatch({
      type: FilterSelectorsTypes.SetSelectedPackage,
      payload: {
        id: item?.id,
        value: item?.id,
        label: item?.code,
        self: item,
      },
    });
    dispatch({
      type: FilterSelectorsTypes.SetFilterToDisplay,
      payload: FiltersType.PACKAGE,
    });

    history.push(`/editor/groups-manager/${contextUrl}`);
  };

  const mainColumns = useMemo(() => {
    return [
      {
        title: t("groups-manager.group-details.columns.group-code"),
        label: group?.clientCode || group?.code || "-",
        skeleton: {
          width: "60px",
          height: "16px",
        },
      },
      {
        title: t("groups-manager.group-details.columns.campus"),
        label: group?.campus?.code,
        skeleton: {
          width: "60px",
          height: "16px",
        },
      },
      {
        title: t("groups-manager.group-details.columns.course-name"),
        label: `${group?.course?.code} ${group?.course?.name}`,
        skeleton: {
          width: "150px",
          height: "16px",
        },
      },
      {
        title: t("groups-manager.group-details.columns.department"),
        label: departments,
        skeleton: {
          width: "100px",
          height: "16px",
        },
      },
      {
        title: t("groups-manager.group-details.columns.school"),
        label: schools,
        skeleton: {
          width: "100px",
          height: "16px",
        },
      },
      {
        title: t("groups-manager.group-details.columns.status"),
        label: group?.isActive
          ? t("groups-manager.groups-table.table.rows.status.active")
          : t("groups-manager.groups-table.table.rows.status.inactive"),
        skeleton: {
          width: "60px",
          height: "16px",
        },
      },
    ];
  }, [group, state?.data?.groups, departments, schools]);

  return (
    <div className={css.groupDetail}>
      <div className={css.groupDetail_content}>
        <header className={css.contentHeader}>
          <div className={css.contentHeader_links}>
            <RoundButton
              variant="ghost"
              icon="chevron-left"
              disabled={isLoadingData}
              onClick={() => {
                dispatch({ type: NavigationTypes.SetForceFetching, payload: true });

                const url =
                  (state?.navigation?.locationStack ?? []).slice(-1)[0]?.view ??
                  `/editor/groups-manager/${contextUrl}`;

                // handle mouseflow
                if (url.includes("groups-manager/subgroups")) {
                  mouseflow.actionTag(
                    "action_groups_manager_subgroups",
                    outerState?.base?.isMouseflowEnabled,
                  );
                } else {
                  mouseflow.actionTag(
                    "action_groups_manager",
                    outerState?.base?.isMouseflowEnabled,
                  );
                }

                history.push(url);
              }}
            />

            <Heading type="h2">
              {isLoadingData ? (
                <SkeletonBase width="180px" height="24px" />
              ) : (
                t("groups-manager.group-details.title", {
                  code: group?.code || "",
                })
              )}
            </Heading>

            <Link
              size="md"
              iconRight="external-link"
              onClick={() => {
                dispatch({
                  type: NavigationTypes.AddToLocationStack,
                  payload: {
                    view: location?.pathname,
                    page: state?.data?.groupsPageInfo?.page ?? 1,
                  },
                });
                history.push(historyChangesUrl);
              }}
            >
              {t("groups-manager.group-details.change-history")}
            </Link>

            <Link
              size="md"
              iconRight="external-link"
              onClick={() => {
                dispatch({
                  type: NavigationTypes.AddToLocationStack,
                  payload: {
                    view: location?.pathname,
                    page: state?.data?.groupsPageInfo?.page ?? 1,
                  },
                });
                history.push(`/editor/packages-edit/${contextUrl}/${group?.link?.id}`);
              }}
            >
              {t("groups-manager.group-details.edit-packages")}
            </Link>
          </div>

          <DonutLegend className={css.contentHeader_legend} data={donut({} as AdaptedGroup)} />
        </header>

        <ul className={css.contentDetails}>
          {mainColumns?.map(column => (
            <li key={column?.title} className={css.contentDetails_item}>
              <Text className={css.itemTitle} type="sm" weight="medium">
                {column?.title}
              </Text>

              {isLoadingData ? (
                <SkeletonBase
                  className={css.itemLabel}
                  width={column?.skeleton?.width}
                  height={column?.skeleton?.height}
                  radius={4}
                />
              ) : (
                <Text className={css.itemLabel}>{column?.label}</Text>
              )}
            </li>
          ))}

          <li className={css.contentDetails_item}>
            <Text className={css.itemTitle} type="sm" weight="medium">
              {t("groups-manager.group-details.columns.capacity")}
            </Text>

            {isLoadingData ? (
              <SkeletonBase className={css.itemLabel} width="60px" height="16px" radius={4} />
            ) : (
              <Tooltip
                className={css.itemTooltip}
                label={t("groups-manager.group-details.columns.capacity-greater-than-max")}
                placement="top"
                visible={displayCapacitiesError ? undefined : false}
              >
                <Text className={css.itemLabel}>{capacitiesSumUp}</Text>
                {displayCapacitiesError && <Icon name="circle-cross" size="sm" color="error-50" />}
              </Tooltip>
            )}
          </li>

          {/* Donut */}
          <li className={css.contentDetails_item}>
            <Donut
              group={group}
              rows={groupsDonutStats}
              donutWidth="40px"
              donutHeight="40px"
              hasTooltip={false}
            />
          </li>
        </ul>
      </div>

      {/* Extra Details */}
      <motion.div
        key="groupExtraDetail"
        layout
        layoutDependency={displayInfo}
        initial={{
          height: 0,
          opacity: 0,
          overflow: "hidden",
        }}
        animate={
          displayInfo
            ? {
                height: "auto",
                opacity: 1,
                overflow: "visible",
              }
            : {
                height: 0,
                opacity: 0,
                overflow: "hidden",
              }
        }
        transition={{
          height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
          opacity: { duration: 0.3, ease: "easeInOut" },
        }}
      >
        <div className={cx(css.groupDetail_extraContent, !displayInfo && css.hide)}>
          <ul className={css.contentDetails}>
            {/* Efficiency */}
            {!isDefaultBehavior && (
              <li className={css.contentDetails_item}>
                <Text className={css.itemTitle} type="sm" weight="medium">
                  {t("groups-manager.group-details.columns.efficiency")}
                </Text>

                <Text className={css.itemLabel}>{efficiency}</Text>
              </li>
            )}

            {/* Capacity */}
            <li className={css.contentDetails_item}>
              <Text className={css.itemTitle} type="sm" weight="medium">
                {t("groups-manager.group-details.columns.calculated-capacity")}
              </Text>

              <Tooltip
                className={css.itemTooltip}
                label={capacityCalculationReasonText(group?.groupCapacity)}
                placement="top"
              >
                <Text className={css.itemLabel}>{group?.groupCapacity?.calculatedCapacity}</Text>
                <Icon name="circle-info" size="sm" color="neutral-70" />
              </Tooltip>
            </li>

            {/* Visibility */}
            <li className={css.contentDetails_item}>
              <Text className={css.itemTitle} type="sm" weight="medium">
                {t("groups-manager.group-details.columns.visibility")}
              </Text>

              <Text className={css.itemLabel}>
                {group?.visibleForEnrollment
                  ? t("groups-manager.general-filters.aditional-filters.visibility.visible")
                  : t("groups-manager.general-filters.aditional-filters.visibility.not-visible")}
              </Text>
            </li>

            {!!group?.link?.packages?.length && (
              <li className={css.contentDetails_item}>
                <Text className={css.itemTitle} type="sm" weight="medium">
                  {t("groups-manager.group-details.columns.packages")}
                </Text>

                <div className={css.itemList}>
                  {group?.link?.packages?.map(item => (
                    <Link
                      key={item?.id}
                      colored
                      className={css.itemLink}
                      onClick={() => handlePackageClick(item)}
                    >
                      {`${item?.code}${item?.name ? ` - ${item?.name}` : ""}`}
                    </Link>
                  ))}
                </div>
              </li>
            )}
          </ul>

          {/* Group schedules */}
          {(group?.scheduleSummary ?? []).length > 0 && (
            <ScheduleSummary
              schedules={group?.scheduleSummary}
              primaryInstructorId={group?.primaryInstructor?.id}
            />
          )}

          {/* Group Term */}
          <section
            className={cx(
              css.contentDetails,
              css.contentDetails__flexColumn,
              css.contentDetails__shortSpacing,
            )}
          >
            <li className={css.contentDetails_item}>
              <Text className={css.itemTitle} type="sm" weight="medium">
                {t("groups-manager.group-details.columns.group-attributes")}:
              </Text>

              <Text className={css.itemLabel}>{groupLabels}</Text>
            </li>

            <li className={css.contentDetails_item}>
              <Text className={css.itemTitle} type="sm" weight="medium">
                {t("groups-manager.group-details.columns.uf-type")}:
              </Text>

              <Text className={css.itemLabel}>{courseTypeName}</Text>
            </li>
          </section>
        </div>
      </motion.div>

      {/* Hide Button */}
      <div
        className={css.groupDetail_trigger}
        role="button"
        onClick={() => !isLoadingData && setDisplayInfo(!displayInfo)}
      >
        <Icon
          className={cx(css.triggerIcon, displayInfo && css.triggerIcon__open)}
          name="chevron-down"
          color="neutral-70"
        />
        <Text className={css.triggerText} weight="medium">
          {displayInfo
            ? t("groups-manager.group-details.hide-additional-info")
            : t("groups-manager.group-details.show-additional-info")}
        </Text>
      </div>
    </div>
  );
};

export default GroupDetail;
